import type { ProductRow } from 'api/canonicalPicker.types'
import type {
  PickerData,
  PickerLevelConfig,
  PickerSelection,
  PickerSource,
} from 'slices/aiConversations/pickerTypes'
import {
  initializeGroup,
  initializePicker,
  setBottomOperator,
  setCanonicalPicks,
  setGroupLevel1Config,
  setGroupLevel2Config,
  setGroupPickerId,
  setGroupSubtitle,
  setGroupTitle,
  setGroupTopOperator,
  sourceChoiceOf,
  startingLevel2Operator,
} from 'slices/aiSegmentBuilder/picker'
import describeUnlabeledPicks, {
  PickerDispatch,
} from 'slices/aiSegmentBuilder/picker/describeUnlabeledPicks'

interface CanonicalLevelConfig extends PickerLevelConfig {
  sources: PickerSource[]
  defaultSource: string
}

/** A picker whose two levels each offer a smart source and a specific one */
export interface CanonicalPickerData extends PickerData {
  level1: CanonicalLevelConfig
  level2: CanonicalLevelConfig
}

const offersSourceChoice = (
  level: PickerLevelConfig | undefined
): level is CanonicalLevelConfig =>
  level !== undefined && sourceChoiceOf(level) !== null

export const isCanonicalPicker = (
  data: PickerData
): data is CanonicalPickerData =>
  offersSourceChoice(data.level1) && offersSourceChoice(data.level2)

const levelConfigOf = (level: CanonicalLevelConfig) => ({
  id: level.id,
  label: level.label,
  operatorOptions: level.operatorOptions ?? [],
  sources: level.sources,
  defaultSource: level.defaultSource,
})

// A label names the pick only when the selection has one value
const level1RowsOf = (selection: PickerSelection): ProductRow[] => {
  const ids = Array.isArray(selection.level1)
    ? selection.level1
    : [selection.level1]
  const label = ids.length === 1 ? selection.level1Label : undefined
  return ids.map(id => ({
    kind: 'product',
    id,
    name: label ?? id,
    variants: null,
  }))
}

const level2RowsOf = (selection: PickerSelection): ProductRow[] =>
  (selection.level2 ?? []).map((id, index) => ({
    kind: 'product',
    id,
    name: selection.level2Labels?.[index] ?? id,
    variants: null,
  }))

/**
 * Loads a canonical picker into the store. thanx-ai preselects from each
 * level's default source and sends the label of every pick, so nothing has
 * to be fetched to show them; variants it may still send are ignored, as
 * whatever needs a pick's versions reads them itself.
 */
const loadCanonicalPickerGroup = (
  dispatch: PickerDispatch,
  groupId: string,
  {
    pickerId,
    title,
    subtitle,
    level1,
    level2,
    selections,
  }: CanonicalPickerData,
  startingPickerId: number
): void => {
  dispatch(initializeGroup({ groupId }))
  dispatch(setGroupTopOperator({ groupId, operator: level1.operator ?? 'any' }))
  dispatch(setGroupPickerId({ groupId, pickerId }))
  dispatch(setGroupTitle({ groupId, title }))
  dispatch(setGroupSubtitle({ groupId, subtitle: subtitle ?? null }))
  dispatch(setGroupLevel1Config({ groupId, ...levelConfigOf(level1) }))
  dispatch(
    setGroupLevel2Config({
      groupId,
      ...levelConfigOf(level2),
      subtitle: level2.subtitle ?? null,
    })
  )

  const level2Options = level2.operatorOptions ?? []
  if (selections.length === 0) {
    dispatch(initializePicker({ groupId, pickerId: startingPickerId }))
    dispatch(
      setBottomOperator({
        pickerId: startingPickerId,
        operator: startingLevel2Operator(level2Options, level2.operator, false),
      })
    )
    return
  }

  const smart1 = sourceChoiceOf(level1)?.smart
  const smart2 = sourceChoiceOf(level2)?.smart
  selections.forEach((selection, index) => {
    const id = startingPickerId + index
    const level1Rows = level1RowsOf(selection)
    const level1Ids = level1Rows.map(row => row.id)
    const unlabeledLevel2 = (selection.level2 ?? []).filter(
      (_, position) => selection.level2Labels?.[position] === undefined
    )

    dispatch(initializePicker({ groupId, pickerId: id }))
    dispatch(
      setBottomOperator({
        pickerId: id,
        operator: startingLevel2Operator(
          level2Options,
          selection.operator ?? level2.operator,
          (selection.level2 ?? []).length > 0
        ),
      })
    )
    dispatch(
      setCanonicalPicks({
        pickerId: id,
        level: 'level1',
        source: level1.defaultSource,
        rows: level1Rows,
      })
    )
    dispatch(
      setCanonicalPicks({
        pickerId: id,
        level: 'level2',
        source: level2.defaultSource,
        rows: level2RowsOf(selection),
      })
    )

    if (smart1 && (level1Ids.length > 1 || !selection.level1Label)) {
      dispatch(
        describeUnlabeledPicks({
          pickerId: id,
          level: 'level1',
          source: smart1,
          ids: level1Ids,
          scope: null,
        })
      )
    }
    if (smart2) {
      dispatch(
        describeUnlabeledPicks({
          pickerId: id,
          level: 'level2',
          source: smart2,
          ids: unlabeledLevel2,
          scope: level1Ids,
        })
      )
    }
  })
}

export default loadCanonicalPickerGroup
